import React, { useState, useEffect } from 'react';
import { getAnomalies, updateAnomalyStatus, analyzeRootCause } from '@/api/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CheckCircle, Search, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const AnomalyList = ({ limit = 50, serviceName = null }) => {
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAnomaly, setSelectedAnomaly] = useState(null);
  const [rootCauseLoading, setRootCauseLoading] = useState(false);
  const [rootCauseData, setRootCauseData] = useState(null);

  useEffect(() => {
    fetchAnomalies();
    const interval = setInterval(fetchAnomalies, 15000); // Refresh every 15 seconds
    return () => clearInterval(interval);
  }, [serviceName]);

  const fetchAnomalies = async () => {
    try {
      const data = await getAnomalies('DETECTED', serviceName, limit);
      setAnomalies(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching anomalies:', error);
      setLoading(false);
    }
  };

  const handleResolve = async (anomalyId) => {
    try {
      await updateAnomalyStatus(anomalyId, 'RESOLVED');
      fetchAnomalies();
    } catch (error) {
      console.error('Error resolving anomaly:', error);
    }
  };

  const handleAnalyze = async (anomaly) => {
    setSelectedAnomaly(anomaly);
    setRootCauseLoading(true);
    setRootCauseData(null);
    
    try {
      const result = await analyzeRootCause(anomaly.id);
      setRootCauseData(result);
      setRootCauseLoading(false);
    } catch (error) {
      console.error('Error analyzing root cause:', error);
      setRootCauseLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DETECTED':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'INVESTIGATING':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'RESOLVED':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getSeverity = (score) => {
    if (score > 0.9) return { label: 'Critical', color: 'bg-red-500' };
    if (score > 0.7) return { label: 'High', color: 'bg-orange-500' };
    if (score > 0.5) return { label: 'Medium', color: 'bg-yellow-500' };
    return { label: 'Low', color: 'bg-blue-500' };
  };

  if (loading) {
    return (
      <div className="text-center py-8" data-testid="anomaly-list-loading">
        <p className="text-gray-500">Loading anomalies...</p>
      </div>
    );
  }

  if (anomalies.length === 0) {
    return (
      <div className="text-center py-8" data-testid="no-anomalies">
        <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
        <p className="text-gray-600">No active anomalies detected</p>
        <p className="text-sm text-gray-500 mt-1">All systems operating normally</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="anomaly-list">
      {anomalies.map((anomaly) => {
        const severity = getSeverity(anomaly.anomaly_score);
        const timestamp = new Date(anomaly.timestamp).toLocaleString();

        return (
          <div
            key={anomaly.id}
            className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
            data-testid={`anomaly-${anomaly.id}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                  <span className="font-semibold text-gray-900">
                    {anomaly.service_name}
                  </span>
                  <span className="text-gray-500">•</span>
                  <span className="text-gray-600">{anomaly.metric_name}</span>
                  <Badge className={`${severity.color} text-white`}>
                    {severity.label}
                  </Badge>
                  <Badge variant="outline" className={getStatusColor(anomaly.status)}>
                    {anomaly.status}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">Actual:</span>
                    <span className="ml-1 font-medium">
                      {anomaly.actual_value.toFixed(2)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Expected:</span>
                    <span className="ml-1 font-medium">
                      {anomaly.predicted_value.toFixed(2)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Score:</span>
                    <span className="ml-1 font-medium">
                      {(anomaly.anomaly_score * 100).toFixed(0)}%
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Time:</span>
                    <span className="ml-1 font-medium text-xs">{timestamp}</span>
                  </div>
                </div>

                <div className="mt-2 text-xs text-gray-500">
                  Detection: {anomaly.detection_method}
                </div>
              </div>

              <div className="flex gap-2 ml-4">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleAnalyze(anomaly)}
                  data-testid={`analyze-btn-${anomaly.id}`}
                >
                  <Search className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleResolve(anomaly.id)}
                  data-testid={`resolve-btn-${anomaly.id}`}
                >
                  <CheckCircle className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        );
      })}

      {/* Root Cause Analysis Dialog */}
      <Dialog open={selectedAnomaly !== null} onOpenChange={() => setSelectedAnomaly(null)}>
        <DialogContent className="max-w-2xl" data-testid="root-cause-dialog">
          <DialogHeader>
            <DialogTitle>Root Cause Analysis</DialogTitle>
            <DialogDescription>
              Analyzing anomaly for {selectedAnomaly?.service_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {rootCauseLoading ? (
              <div className="text-center py-8">
                <Search className="w-12 h-12 animate-spin mx-auto text-blue-500 mb-3" />
                <p className="text-gray-600">Analyzing traces and dependencies...</p>
              </div>
            ) : rootCauseData ? (
              <div>
                <h4 className="font-semibold mb-3">Root Cause Components:</h4>
                <div className="space-y-2">
                  {rootCauseData.root_cause_components.map((component, idx) => (
                    <div
                      key={idx}
                      className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg"
                    >
                      <AlertTriangle className="w-5 h-5 text-red-600 mr-3" />
                      <span className="font-medium text-red-900">{component}</span>
                    </div>
                  ))}
                </div>
                
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-900">
                    <strong>Analysis Time:</strong>{' '}
                    {new Date(rootCauseData.analyzed_at).toLocaleString()}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-gray-600">Click analyze to identify root cause</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AnomalyList;
